import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GlobalNotificationService } from '../../../../services/globalnotification.service';
import { IORD_Order, ReturnViewModel } from '../../../../interfaces/request.interface';
import { RTAApiClient } from '../../../../services/rta-api.service';
import { FileVaultApiService } from '../../../../services/filevault-api.service';
import { chevronDoubleRightIcon } from '@progress/kendo-svg-icons';

@Component({
    selector: 'app-create-material-master-bits',
    templateUrl: './create-material-master-bits.component.html',
    styleUrls: ['./create-material-master-bits.component.css'],
    standalone: false
})
export class CreateMaterialMasterBitsComponent implements OnChanges {
    @Input() open = false;
    @Input() request?: IORD_Order;
    @Output() closed = new EventEmitter<void>();
    @Output() created = new EventEmitter<ReturnViewModel>();

    public form: FormGroup;
    public submitting = false;
    public showDialog = false;

    constructor(
        private fb: FormBuilder,
        private rtaApi: RTAApiClient,
        private fileVaultApi: FileVaultApiService,
        private notification: GlobalNotificationService
    ) {
        this.form = this.fb.group({
            materialWeight: [null],
            bitLevel: [null],
            bit_Productline: [null, Validators.required],
            bit_SubPsl: [null, Validators.required],
            size: [null, Validators.required],
            coreSize: [null, Validators.required],
            bitType: [null, Validators.required],
            hasSleeve: [false],
            mfgPlant: [2130, Validators.required],
            isJV: [false],
            requestTargetId: [null, Validators.required]
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['request']) {
            this.applyDefaults();
        }
        if (changes['open'] && this.open) {
            this.applyDefaults();
            if (this.form.valid) {
                this.submit();
            } else {
                this.showDialog = true;
            }
        }
        if (changes['open'] && !this.open) {
            this.showDialog = false;
        }
    }

    applyDefaults(): void {
        if (!this.request) {
            return;
        }

        const defaults = {
            bit_Productline: this.request.productLine ?? null,
            bit_SubPsl: this.request.subPsl ?? null,
            size: this.request.bit?.size ?? null,
            coreSize: this.request.bit?.coreSize ?? 0,
            bitType: this.request.bit?.type ?? null,
            bitLevel: this.request.bit?.level ?? null,
            hasSleeve: this.request.bit?.sleeve ?? false,
            requestTargetId: this.request.id ?? null,
            materialWeight: this.request.bitWeight ?? null
        };

        Object.entries(defaults).forEach(([key, value]) => {
            const control = this.form.get(key);
            if (!control) {
                return;
            }
            const current = control.value;
            if (current === null || current === undefined || current === '') {
                control.patchValue(value, { emitEvent: false });
            }
        });
    }

    cancel(): void {
        this.showDialog = false;
        this.closed.emit();
    }

    submit(): void {
        if (this.form.invalid || this.submitting) {
            return;
        }

        this.submitting = true;
        const raw = this.form.value;
        const materialWeight = raw.materialWeight != null ? String(raw.materialWeight) : null;
        const mfgPlant = raw.mfgPlant != null ? String(raw.mfgPlant) : null;
        const payload = {
            MaterialWeight: materialWeight,
            BitLevel: raw.bitLevel,
            Bit_Productline: raw.bit_Productline,
            Bit_SubPsl: raw.bit_SubPsl,
            Size: raw.size,
            CoreSize: raw.coreSize,
            BitType: raw.bitType,
            HasSleeve: raw.hasSleeve,
            MfgPlant: mfgPlant,
            IsJV: raw.isJV,
            UserId: this.rtaApi.$CURRENT_USER_ID,
            RequestTargetId: raw.requestTargetId ?? this.request?.id,
            materialWeight: materialWeight,
            bitLevel: raw.bitLevel,
            bit_Productline: raw.bit_Productline,
            bit_SubPsl: raw.bit_SubPsl,
            size: raw.size,
            coreSize: raw.coreSize ?? 0,
            bitType: raw.bitType,
            hasSleeve: raw.hasSleeve,
            mfgPlant: mfgPlant,
            isJV: raw.isJV,
            userId: this.rtaApi.$CURRENT_USER_ID,
            requestTargetId: raw.requestTargetId ?? this.request?.id
        } as any;
        this.notification.showInfoNotification('Material master request has been submitted. Please wait...');
        this.rtaApi.createMaterialMasterBits(payload).subscribe({
            next: (response) => {
                this.submitting = false;
                if (response?.status) {
                    this.notification.showSuccessNotification(response.message ?? 'Material master bit created.');
                    this.createFileVaultPart(response);
                    this.created.emit(response);
                    this.form.reset({ hasSleeve: false, isJV: false });
                    this.applyDefaults();
                    this.showDialog = false;
                    this.closed.emit();
                } else {
                    this.notification.showWarningNotification(response?.message ?? 'Unable to create material master bit.');
                }
            },
            error: () => {
                this.submitting = false;
                this.notification.showErrorNotification('Failed to create material master bit.');
            }
        });
    }

    private createFileVaultPart(response: ReturnViewModel | undefined): void {
        const materialNumber = this.extractMaterialNumber(response);
        if (!materialNumber) {
            return;
        }

        const requestId = this.request?.id;
        const description = requestId
            ? `Auto-created from SAP material master for RTA ${requestId}.`
            : 'Auto-created from SAP material master.';

        this.fileVaultApi.createPart({
            partNumber: materialNumber,
            name: materialNumber,
            description,
            attributes: requestId ? { sourceSystem: 'SAP', rtaRequestId: requestId } : { sourceSystem: 'SAP' }
        }).subscribe({
            error: (error) => {
                if (error?.status === 409) {
                    this.notification.showWarningNotification(`FileVault part ${materialNumber} already exists.`);
                    return;
                }
                this.notification.showWarningNotification('Material master created but FileVault part creation failed.');
            }
        });
    }

    private extractMaterialNumber(response: ReturnViewModel | undefined): string | null {
        const messageValue = typeof response?.message === 'string' ? response.message.trim() : '';
        if (messageValue) {
            return messageValue;
        }

        const info = response?.info as any;
        const candidate = info?.materialNumber ?? info?.MaterialNumber ?? info?.sapNumber ?? info?.SapNumber;
        if (typeof candidate === 'number') {
            return String(candidate);
        }
        if (typeof candidate === 'string' && candidate.trim()) {
            return candidate.trim();
        }
        return null;
    }
}
